import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Card, Grid, TextField, Typography } from "@mui/material";
import { getHttpClient } from "src/services/rest-api";
import { HTTPService } from "src/services/httpservice";

const currencySymbol = (currency: string) => {
  if (currency === "GBP") return "£";
  if (currency === "INR") return "₹";
  if (currency === "AUD") return "A$";
  if (currency === "CAD") return "C$";
  if (currency === "NZD") return "NZ$";
  if (currency === "EUR") return "€";
  return "$";
};

const ScheduleMessagingPricing = () => {
  const [messageSettings, setMessageSettings] = useState({
    schedule_message_included_allowance: "300",
    schedule_message_overage_fee: "0.001",
    schedule_message_overage_currency: "GBP",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getHttpClient(HTTPService.APPADMIN_SCHEDULE_MESSAGING_SETTINGS, "GET", null, null, null);
      setMessageSettings({
        schedule_message_included_allowance: response?.data?.schedule_message_included_allowance ?? "300",
        schedule_message_overage_fee: response?.data?.schedule_message_overage_fee ?? "0.001",
        schedule_message_overage_currency: response?.data?.schedule_message_overage_currency ?? "GBP",
      });
    } catch (err: any) {
      setError(err?.message || "Failed to load schedule messaging pricing.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const save = async () => {
    try {
      setSaving(true);
      setError(null);
      await getHttpClient(HTTPService.APPADMIN_SCHEDULE_MESSAGING_SETTINGS, "PUT", messageSettings, null, null);
      setSuccess("Schedule messaging pricing saved.");
    } catch (err: any) {
      setError(err?.message || "Failed to save schedule messaging pricing.");
    } finally {
      setSaving(false);
    }
  };

  const includedAllowance = Number(messageSettings.schedule_message_included_allowance || 0);
  const overageFee = Number(messageSettings.schedule_message_overage_fee || 0);
  const currency = messageSettings.schedule_message_overage_currency || "GBP";
  const summary =
    includedAllowance > 0
      ? `${includedAllowance} automated notifications included per store/month. Additional automated notifications are billed at ${currencySymbol(currency)}${overageFee}/message, excluding Meta WhatsApp charges billed directly by Meta.`
      : `No automated notifications are included per store/month. Automated notifications are billed from the first message at ${currencySymbol(currency)}${overageFee}/message, excluding Meta WhatsApp charges billed directly by Meta.`;

  return (
    <Card
      variant="outlined"
      sx={{
        p: 3,
        borderRadius: "20px",
        borderColor: "divider",
        backgroundColor: "#fff",
        boxShadow: "0 8px 32px rgba(0,0,0,0.03)",
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap" mb={2}>
        <Box>
          <Typography fontSize="18px" fontWeight={800}>
            Schedule Messaging Pricing
          </Typography>
          <Typography fontSize="13px" color="text.secondary">
            Configure TYFHU service allowance and overage shown across scheduling, public pricing, and usage screens.
          </Typography>
        </Box>
        <Button variant="contained" onClick={save} disabled={saving || loading}>
          {saving ? "Saving..." : "Save pricing"}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: "12px" }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Included notifications/store/month"
            value={messageSettings.schedule_message_included_allowance}
            onChange={(e) => setMessageSettings((prev) => ({ ...prev, schedule_message_included_allowance: e.target.value }))}
            helperText="Use 0 to charge from the first automated notification."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Fee per extra notification"
            value={messageSettings.schedule_message_overage_fee}
            onChange={(e) => setMessageSettings((prev) => ({ ...prev, schedule_message_overage_fee: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            SelectProps={{ native: true }}
            fullWidth
            label="Currency"
            value={messageSettings.schedule_message_overage_currency}
            onChange={(e) => setMessageSettings((prev) => ({ ...prev, schedule_message_overage_currency: e.target.value }))}
          >
            <option value="GBP">GBP</option>
            <option value="USD">USD</option>
            <option value="AUD">AUD</option>
            <option value="CAD">CAD</option>
            <option value="NZD">NZD</option>
            <option value="EUR">EUR</option>
            <option value="INR">INR</option>
          </TextField>
        </Grid>
      </Grid>

      <Alert severity="info" sx={{ mt: 2, borderRadius: "12px" }}>
        {summary}
      </Alert>
    </Card>
  );
};

export default ScheduleMessagingPricing;
